import { RegionType, ShopFilter } from "@b2bportal/air-shopping-api";
import { type AirChfarExerciseShopRequest } from "@b2bportal/air-chfar-api";
import { useMemo } from "react";
import { FlightShopType } from "redmond";
import { useGetFlightsQuery } from "../reducers/flights/api";
import { useChfarExerciseParams } from "./useChfarExerciseParams";
import { useChfarPolicy } from "./useChfarPolicy";

export const useFlightList = () => {
  const {
    itineraryId,
    contractId,
    origin,
    departureDate,
    originRegionType,
    destination,
    returnDate,
    destinationRegionType,
    flightShopType,
  } = useChfarExerciseParams();

  const skip =
    flightShopType !== FlightShopType.CHANGE_FOR_ANY_REASON_EXERCISE ||
    itineraryId == null ||
    contractId == null;

  const { data: chfarPolicy } = useChfarPolicy(
    { itineraryId: itineraryId as string, contractId: contractId as string },
    skip
  );
  const adultsCount = Number(chfarPolicy?.passengers.adults ?? 1);
  const childrenCount = Number(chfarPolicy?.passengers.children ?? 0);
  const infantsInSeatCount = Number(chfarPolicy?.passengers.infantInSeat ?? 0);
  const infantsOnLapCount = Number(chfarPolicy?.passengers.infantInLap ?? 0);

  const passengers = useMemo(
    () => ({
      ADT: adultsCount,
      ...(childrenCount !== 0 && { CNN: childrenCount }),
      ...(infantsInSeatCount !== 0 && { INS: infantsInSeatCount }),
      ...(infantsOnLapCount !== 0 && { INF: infantsOnLapCount }),
    }),
    [adultsCount, childrenCount, infantsInSeatCount, infantsOnLapCount]
  );

  const paramsValid = Boolean(
    origin &&
      departureDate &&
      originRegionType &&
      destination &&
      destinationRegionType &&
      itineraryId &&
      contractId &&
      flightShopType === FlightShopType.CHANGE_FOR_ANY_REASON_EXERCISE
  );

  const request = useMemo(
    () =>
      ({
        // We're checking the params validity above to avoid sending an invalid request to the API
        itineraryId: itineraryId as string,
        contractId: contractId as string,
        rebookingShopFilters: {
          type: "ShopSummaryRequest",
          value: {
            route: {
              origin: {
                code: origin,
                regionType:
                  originRegionType === "city"
                    ? RegionType.city
                    : RegionType.airport,
              },
              destination: {
                code: destination,
                regionType:
                  destinationRegionType === "city"
                    ? RegionType.city
                    : RegionType.airport,
              },
            },
            departureDate,
            returnDate,
            passengers,
            tripFilter: ShopFilter.NoFilter,
          },
        },
      } satisfies AirChfarExerciseShopRequest),
    [
      origin,
      departureDate,
      originRegionType,
      destination,
      returnDate,
      passengers,
      destinationRegionType,
      itineraryId,
      contractId,
    ]
  );

  const { isLoading, isError, isFetching, data } = useGetFlightsQuery(request, {
    skip: !paramsValid,
  });

  return useMemo(
    () => ({ isLoading, isError, isFetching, data }),
    [isLoading, isError, isFetching, data]
  );
};
